import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTable, useCustom } from '@refinedev/core';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ListPage, EditButton, ShowButton, DeleteButton } from '@/components/crud';
import { MapPin, Building2, Store, Globe } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { rowOpenProps } from '@/lib/rowOpen';
import { ExportDropdown } from '../../components/common/ExportDropdown';
import { exportRich, type RichFormat } from '../../utils/richExport';

interface Address {
  id: number;
  address_type: 'headquarters' | 'branch' | 'warehouse' | 'billing' | 'shipping' | 'other';
  address_type_display: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  company: number;
  company_name?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  full_address: string;
}

const addressTypeConfig: Record<string, { icon: React.ElementType; variant: "info" | "success" | "warning" | "secondary" | "destructive" }> = {
  headquarters: { icon: Building2, variant: "info" },
  branch: { icon: Store, variant: "success" },
  warehouse: { icon: MapPin, variant: "warning" },
  billing: { icon: MapPin, variant: "secondary" },
  shipping: { icon: MapPin, variant: "info" },
  other: { icon: MapPin, variant: "secondary" },
};

export const AddressManagementList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { tableQuery } = useTable<Address>({
    resource: 'addresses',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  });

  const statsQuery = useCustom({ url: '/core/addresses/statistics/', method: 'get' });
  const statisticsData: any = statsQuery.data?.data || {};

  const records = tableQuery?.data?.data || [];

  // The rows the screen is actually showing — the export must match the search.
  const visible = records.filter((r) => !search
    || r.full_address?.toLowerCase().includes(search.toLowerCase())
    || r.line1?.toLowerCase().includes(search.toLowerCase()));

  const exportAddresses = async (format: RichFormat) => {
    await exportRich(format, {
      fileName: 'addresses',
      title: 'Addresses',
      subtitle: 'Registered company, branch and delivery addresses',
      meta: search ? { Search: `"${search}"` } : undefined,
      columns: [
        'Type', { header: 'Address Line 1', width: 34 }, 'Address Line 2', 'City',
        'State', 'Postal Code', 'Country', 'Company', { header: 'Status', kind: 'status' },
      ],
      rows: visible.map((r) => [
        r.address_type_display || r.address_type, r.line1, r.line2 || '', r.city,
        r.state, r.postal_code, r.country, r.company_name || '',
        r.status === 'active' ? 'Active' : 'Inactive',
      ]),
    });
  };

  return (
    <div>
      <PageHeader title="Address Management" icon={<MapPin className="h-6 w-6" />} />

      {/* Statistics Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard label="Total Addresses" value={statisticsData.total_addresses || 0} icon={MapPin} tone="brand" />
        <StatCard label="Headquarters" value={statisticsData.addresses_by_type?.headquarters || 0} icon={Building2} tone="brand" />
        <StatCard label="Branches" value={statisticsData.addresses_by_type?.branch || 0} icon={Store} tone="brand" />
        <StatCard label="Countries" value={Object.keys(statisticsData.addresses_by_country || {}).length} icon={Globe} tone="brand" />
      </div>

      <ListPage title="Addresses">
        {/* Search */}
        <div className="flex items-center gap-4 mb-4">
          <Input
            placeholder="Search addresses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <div className="flex gap-2 ml-auto">
            <ExportDropdown size="default" buttonText="Export" options={{ data: visible, filename: 'addresses', formats: ['excel', 'pdf', 'csv', 'json'], customExportHandler: (f) => exportAddresses(f as RichFormat) }} />
            <Button variant="outline">Bulk Validate</Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-3 text-left font-medium">Type</th>
                <th className="p-3 text-left font-medium">Address</th>
                <th className="p-3 text-left font-medium">Company</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Created</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((record) => {
                const config = addressTypeConfig[record.address_type] || addressTypeConfig.other;
                const Icon = config.icon;
                return (
                  <tr key={record.id} {...rowOpenProps(() => navigate(`/core/addresses/edit/${record.id}`), 'border-b hover:bg-muted/50')}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <Badge variant={config.variant}>{record.address_type_display}</Badge>
                      </div>
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-sm">{record.line1}</p>
                        {record.line2 && <p className="text-xs text-muted-foreground">{record.line2}</p>}
                        <p className="text-xs text-muted-foreground">{record.city}, {record.state} {record.postal_code}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Globe className="h-3 w-3" />{record.country}
                        </p>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm">{record.company_name || "No company assigned"}</span>
                    </td>
                    <td className="p-3">
                      <Badge variant={record.status === 'active' ? 'success' : 'secondary'}>
                        {record.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {new Date(record.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <ShowButton recordItemId={record.id} hideText size="icon" />
                        <EditButton recordItemId={record.id} hideText size="icon" />
                        <DeleteButton recordItemId={record.id} hideText size="icon" />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {records.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No addresses found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </ListPage>
    </div>
  );
};

export default AddressManagementList;
